import {
  ClinicalFormDetails,
  ClinicalFormInfo,
  MemberAnswerInfo,
  MemberFormInfo,
  MemberFormStatus,
} from "clinical-forms-experience-client";
import React, { useEffect, useRef, useState } from "react";
import {
  assignClinicalForm,
  getOneClinicalForm,
  getAllClinicalForms,
  getOneMemberForm,
  useAssessmentParams,
  getAllMemberFormsStatuses,
  getAllMemberForms,
  saveMemberAnswer,
  getMemberFormAnswers,
  updateMemberFormStatus,
} from "./apiUtils.assessment";
import { getScoreDiff, navigateToAssessment } from "./utils.assessment";
import { formatDate } from "../../helper/dateFormatter";
import AssessmentSideNavBar from "./SideNavBar.assessment";
import ClinicalFormDetailsView from "./Details.clinical-forms";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import AssessmentsFooter from "./Footer.assessments";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { setLastAssessmentDate } from "../../store/slices/lastAssessmentDate";
import { useDispatch } from "react-redux";
import AssessmentPGIs from "./PGIs.assessment";
import { Problem } from "clinical-forms-experience-client";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import { PgiRequest } from "pgi-experience-client";
import useLoggedInAccount from "../../auth/LoggedInAccountInfo";
import { createUsersApi } from "../../api-helpers/UserApiHelper";
import { ToastConfig } from "../../components/Toaster";
import { UsersDto } from "users-experience-client";
import { createPgiApi } from "../../api-helpers/PgiApiHelper";
import { showToast } from "../../store/slices/toastSlice";
import { useAppointmentActivityLogger } from "../../hooks/useAppointmentActivityLogger";
import {
  AppointmentActivityAction,
  AppointmentActivityType,
} from "../../constants/AppointmentActivity";

const AssessmentForm = () => {
  const [memberForm, setMemberForm] = useState<MemberFormInfo | null>(null);
  const [formStatuses, setFormStatuses] = useState<Map<string, MemberFormStatus>>(new Map());
  const [allAnswersGiven, setAllAnswersGiven] = useState(false);
  const [answers, setAnswers] = useState<MemberAnswerInfo[]>([]);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);
  const [user, setUser] = useState<UsersDto>();
  const pgiApi = createPgiApi();

  const [selectedProblems, setSelectedProblems] = useState<Problem[]>([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const clinicalFormsApi = createClinicalFormsApi();
  const userApi = createUsersApi();

  const [searchParams] = useSearchParams();

  const { loggedInAccount } = useLoggedInAccount();

  const { patientId, assessmentTypeId, memberFormId, assessmentId, editPgis, pastAssessment:isPastAssessment } =
    useAssessmentParams();

  const logAppointmentActivity = useAppointmentActivityLogger();
  const loggedResumedFormIds = useRef(new Set<string>());
  const logAssessmentActivity = (
    form: MemberFormInfo,
    action: AppointmentActivityAction
  ) => {
    void logAppointmentActivity({
      patientId: patientId!,
      activityType: AppointmentActivityType.ASSESSMENT,
      refId: form.id,
      action,
      title: form.title,
      metadata: { formId: form.formId },
    });
  };

  const handleAnswerChange = async (
    questionId: string,
    answer: MemberAnswerInfo
  ) => {
    const request = {
      answer: answer.answer,
      notes: answer.notes,
    };

    // Assign the form if not assigned
    if (!memberFormId) {
      LoadingHelper.showLoader("main");
      const assignedForm = await assignClinicalForm(
        assessmentTypeId!,
        patientId!,
        assessmentId!
      );
      if (assignedForm?.data) {
        setMemberForm(assignedForm.data);
        loggedResumedFormIds.current.add(assignedForm.data.id);
        logAssessmentActivity(assignedForm.data, AppointmentActivityAction.STARTED);

        // Save the answer to the assigned form
        await saveMemberAnswer(request, assignedForm.data.id, questionId);
        setLastSaveTime(new Date());

        navigate(
          `/patients/${patientId}/assessments/${assessmentTypeId}/resume/${assignedForm.data.id}`
        );
      }
      LoadingHelper.hideLoader("main");
    } else {
      // Save the answer to the existing form
      await saveMemberAnswer(request, memberFormId, questionId);
      setLastSaveTime(new Date());
      if (memberForm && !loggedResumedFormIds.current.has(memberForm.id)) {
        loggedResumedFormIds.current.add(memberForm.id);
        logAssessmentActivity(memberForm, AppointmentActivityAction.UPDATED);
      }
    }
  };

  useEffect(() => {
    if (!loggedInAccount) {
      return;
    }

    const loadUser = async () => {
      const userResponse = await userApi.usersControllerFindOne(
        loggedInAccount.username
      );
      setUser(userResponse.data);
    };

    loadUser();
  }, [loggedInAccount]);

  async function submitPgis(): Promise<void> {
    LoadingHelper.showLoader("main");
    try {
      const pgiRequests: PgiRequest[] = [];
      const addedGoalComments = new Set<string>();

      selectedProblems?.forEach((problem) => {
        if (!problem.goals?.length) return;

        problem.goals.forEach((goal) => {
          if (!goal.interventions?.length) return;

          goal.interventions.forEach((intervention) => {
            const isCommentAddedForGoal = addedGoalComments.has(goal.id);
            // Add the goal comment only for the first intervention of the goal
            const goalComment = !isCommentAddedForGoal
              ? `${memberForm?.title}`
              : "";
            if (!isCommentAddedForGoal) {
              addedGoalComments.add(goal.id);
            }
            pgiRequests.push({
              interventionId: intervention.id,
              goalId: goal.id,
              problemId: problem.id,
              clinicalFormVersionId: memberForm?.id,
              goalDueDate:
                goal.defaultDueDays != null
                  ? (() => {
                      const currentDate = new Date();
                      const utcDate = new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                      utcDate.setUTCDate(
                        utcDate.getUTCDate() + goal.defaultDueDays
                      );
                      return utcDate;
                    })()
                  : (() => {
                      const currentDate = new Date();
                      return new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                    })(),
              goalDesignerId: "",
              interventionDesignerId: "",
              goalDesignerStateId: "",
              interventionDueDate:
                intervention.defaultDueDays != null
                  ? (() => {
                      const currentDate = new Date();
                      const utcDate = new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                      utcDate.setUTCDate(
                        utcDate.getUTCDate() + intervention.defaultDueDays
                      );
                      return utcDate;
                    })()
                  : (() => {
                      const currentDate = new Date();
                      return new Date(
                        Date.UTC(
                          currentDate.getUTCFullYear(),
                          currentDate.getUTCMonth() + 1,
                          currentDate.getUTCDate()
                        )
                      );
                    })(),
              assigneeUserId: user!.id,
              interventionDesignerStateId: "",
              problemDesignerId: "",
              problemDesignerStateId: "",
              goalComment,
              interventionComment: `${memberForm?.title}`,
              generatedByAssessment: true,
            });
          });
        });
      });

      try {
        await pgiApi.pgiControllerAddPgi(pgiRequests, patientId!);
      } catch (error: any) {
        const errorMessage = error?.response?.data?.message?.message?.message;
        dispatch(
          showToast({
            type: "danger",
            title: errorMessage,
            message: "",
            icon: "error_outline",
          })
        );
      }
      // If PGIs are displayed from past assessment than navigate to the history else navigate to the pgi
      isPastAssessment 
        ? navigate(`/patients/${patientId}/assessments/${assessmentTypeId}/history`) 
        : navigate(`/patients/${patientId}/pgi`);
    } finally {
      LoadingHelper.hideLoader("main");
    }
  }

  const getData = async () => {
    LoadingHelper.showLoader("main");
    // Get all clinical Forms and form statuses
    const formStatusesResponse = await getAllMemberFormsStatuses(patientId!);
    if (formStatusesResponse?.data) {
      const formStatuses = new Map<string, MemberFormStatus>();
      formStatusesResponse.data.forEach((status) =>
        formStatuses.set(status.formId, status)
      );
      setFormStatuses(formStatuses);
    }

    if (memberFormId) {
      getMemberForm(memberFormId);

      const memberFormAnswersResponse = await getMemberFormAnswers(memberFormId);
      setAnswers(memberFormAnswersResponse?.data.formAnswers || []);
    }
    LoadingHelper.hideLoader("main");
  };

  const handleFormSubmit = async () => {
    try {
      await clinicalFormsApi.clinicalFormsControllerUpdateMemberFormStatus(
        { value: ClinicalFormsConstants.submittedStatus },
        memberForm!.id!
      );
      memberForm!.status = ClinicalFormsConstants.submittedStatus;
      logAssessmentActivity(memberForm!, AppointmentActivityAction.COMPLETED);
      setMemberForm(Object.assign({}, memberForm));
      navigate(
        `/patients/${patientId}/assessments/${assessmentTypeId}/resume/${memberFormId}?editPgis=true`
      );
    } catch {
      dispatch(
        showToast({
          type: "danger",
          title: "Unable to submit the assessment.",
          message: "",
          icon: "error_outline",
        })
      );
    } finally {
      dispatch(setLastAssessmentDate(formatDate(new Date())));
    }
  };

  const getMemberForm = async (memberFormId:string) => {
    const memberFormResponse = await getOneMemberForm(memberFormId!);
    setMemberForm(memberFormResponse?.data || null);
    setLastSaveTime(() => memberFormResponse?.data?.updatedOn || null);
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    setAnswers([])
    if (!memberFormId) {
      setLastSaveTime(null);
      return;
    };
    getMemberForm(memberFormId);
    getMemberFormAnswers(memberFormId).then((response) => {
      setAnswers(response?.data.formAnswers || []);
    });
  }, [memberFormId, assessmentTypeId]);

  return (
    <>
      <div className="main assessment">
        {/* Assessment Aside Navigation */}
        <AssessmentSideNavBar
          memberForm={memberForm}
          setAllAnswersGiven={setAllAnswersGiven}
        />

        {/* Render Assessment Questions */}
        {editPgis ? (
          <div className="assessment__main">
            <div className="">
              <div className="text-light fs-11">MODULE</div>
              <h6>{memberForm?.title}</h6>
            </div>
            <AssessmentPGIs setSelectedProblems={setSelectedProblems} />
          </div>
        ) : (
          <ClinicalFormDetailsView
            formId={assessmentTypeId || ""}
            comments={memberForm?.comments || ""}
            commentsPosition="right"
            readOnly={!!(memberFormId && memberForm?.status === ClinicalFormsConstants.submittedStatus)}
            answers={answers}
            onAnswersChange={handleAnswerChange}
            onAllAnswersGivenChange={setAllAnswersGiven}
            onCommentsChange={() => {}}
            title={memberForm?.title}
            hideComments={true}
          >
            {(() => {
              const status = formStatuses.get(memberForm?.formId || "");
              return (
                <div className="d-flex flex-wrap gap-4 mt-3 fs-13">
                  <div>
                    <div className="text-light fs-11 text-uppercase mb-1">
                      Last Value
                    </div>
                    <div className="fw-semibold">
                      {status?.currentScore ?? "—"}
                      {getScoreDiff(status)}
                    </div>
                  </div>
                  <div>
                    <div className="text-light fs-11 text-uppercase mb-1">
                      Last Completion
                    </div>
                    <div className="fw-semibold">
                      {status?.lastSubmittedOn
                        ? formatDate(status.lastSubmittedOn)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-light fs-11 text-uppercase mb-1">
                      Status
                    </div>
                    <div className="fw-semibold">
                      {status?.isOverDue ? (
                        <span className="badge bg-danger">Overdue</span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-light fs-11 text-uppercase mb-1">
                      Due Date
                    </div>
                    <div className="fw-semibold">
                      {status?.nextDueDate
                        ? formatDate(status.nextDueDate)
                        : "—"}
                    </div>
                  </div>
                </div>
              );
            })()}
          </ClinicalFormDetailsView>
        )}
      </div>

      <AssessmentsFooter
        lastSaveTime={lastSaveTime}
        memberForm={memberForm}
        allAnswersGiven={allAnswersGiven}
        handleFormSubmit={handleFormSubmit}
        submitPgis={submitPgis}
      />
    </>
  );
};

export default AssessmentForm;
